import { useEffect, useRef } from 'react';
import { loadGoogleMaps } from '@/lib/googleMaps';

/**
 * Drop-in replacement for the old Leaflet `<MapContainer>` we used before.
 * Renders a Google Map with a single modern `AdvancedMarkerElement` at
 * (lat, lng).
 *
 * Props:
 *   - lat, lng (required, numbers)
 *   - zoom (default 14)
 *   - title (marker tooltip)
 *   - mapId (optional Google Cloud Map ID; falls back to the free DEMO id)
 *   - className / style — applied to the container div
 */
export function GoogleMapView({ lat, lng, zoom = 14, title = '', mapId, className = '', style }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (lat == null || lng == null) return undefined;
    (async () => {
      try {
        const { google } = await loadGoogleMaps();
        if (cancelled || !elRef.current) return;
        const { Map } = await google.maps.importLibrary('maps');
        const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
        if (cancelled || !elRef.current) return;
        const center = { lat: Number(lat), lng: Number(lng) };
        if (!mapRef.current) {
          mapRef.current = new Map(elRef.current, {
            center,
            zoom,
            mapId: mapId || 'DEMO_MAP_ID',
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            gestureHandling: 'greedy',
          });
          markerRef.current = new AdvancedMarkerElement({ position: center, map: mapRef.current, title });
        } else {
          mapRef.current.setCenter(center);
          mapRef.current.setZoom(zoom);
          if (markerRef.current) markerRef.current.position = center;
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('GoogleMapView failed to load:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [lat, lng, zoom, title, mapId]);

  return (
    <div
      ref={elRef}
      data-testid="google-map-view"
      className={className}
      style={{ height: '100%', minHeight: '320px', width: '100%', ...(style || {}) }}
    />
  );
}
